/**
 * RummyEngine — Core game logic for Indian Rummy (13-Card Rummy)
 *
 * Handles: deck creation, dealing, drawing, discarding, declaration validation,
 * meld detection, and scoring.
 */

import {
  Card, Suit, Rank, Meld,
  RummyGameState, RummyGameConfig, RummyPlayerState,
  VisibleRummyState,
  RummyAction, ActionResult, DeclarationResult,
  SUITS, RANKS, RANK_VALUES, CARD_POINTS,
  CARDS_PER_HAND, JOKER_COUNT, DECK_COUNT, MIN_MELD_SIZE,
  REQUIRED_SEQUENCES,
} from './types';

export class RummyEngine {
  private state: RummyGameState;

  constructor() {
    this.state = this.createEmptyState();
  }

  // ===========================================================================
  // Initialization
  // ===========================================================================

  private createEmptyState(): RummyGameState {
    return {
      players: [],
      stock: [],
      discardPile: [],
      wildJoker: null,
      currentPlayerIndex: 0,
      turnPhase: 'draw',
      gameStatus: { type: 'waiting' },
      roundNumber: 0,
      discardHistory: [],
    };
  }

  initializeGame(config: RummyGameConfig): void {
    const players: RummyPlayerState[] = config.players.map(p => ({
      id: p.id,
      name: p.name,
      hand: [],
      melds: [],
      isHuman: p.isHuman,
      aiDifficulty: p.aiDifficulty,
      score: 0,
      status: 'active',
      hasDrawn: false,
    }));

    this.state = {
      ...this.createEmptyState(),
      players,
    };
  }

  startNewRound(): void {
    const deck = this.createDeck();
    this.shuffleDeck(deck);

    // Reset player hands and status
    for (const player of this.state.players) {
      player.hand = [];
      player.melds = [];
      player.status = 'active';
      player.hasDrawn = false;
    }

    // Deal 13 cards to each player
    for (let i = 0; i < CARDS_PER_HAND; i++) {
      for (const player of this.state.players) {
        const card = deck.pop();
        if (card) player.hand.push(card);
      }
    }

    // Select wild joker from remaining deck
    const wildJokerCard = deck.pop()!;
    const wildJokerRank = wildJokerCard.isJoker ? null : wildJokerCard.rank;

    // Mark wild jokers in all cards
    if (wildJokerRank) {
      const markWild = (card: Card) => {
        if (card.rank === wildJokerRank && !card.isJoker) {
          card.isWildJoker = true;
        }
      };
      deck.forEach(markWild);
      for (const player of this.state.players) {
        player.hand.forEach(markWild);
      }
      wildJokerCard.isWildJoker = true;
    }

    // Place one card face-up as the discard pile start
    const firstDiscard = deck.pop()!;
    
    this.state.stock = deck;
    this.state.discardPile = [firstDiscard];
    this.state.wildJoker = wildJokerCard;
    this.state.currentPlayerIndex = 0;
    this.state.turnPhase = 'draw';
    this.state.roundNumber++;
    this.state.gameStatus = { type: 'active' };
    this.state.discardHistory = [firstDiscard];
  }

  // ===========================================================================
  // Deck Creation
  // ===========================================================================

  private createDeck(): Card[] {
    const cards: Card[] = [];
    let idCounter = 0;

    // Create 2 standard decks
    for (let d = 0; d < DECK_COUNT; d++) {
      for (const suit of SUITS) {
        for (const rank of RANKS) {
          cards.push({
            suit,
            rank,
            id: `${suit}-${rank}-${d}-${idCounter++}`,
            isJoker: false,
            isWildJoker: false,
          });
        }
      }
    }

    // Add printed jokers
    for (let j = 0; j < JOKER_COUNT; j++) {
      cards.push({
        suit: 'spades' as Suit,
        rank: 'A' as Rank,
        id: `joker-${j}-${idCounter++}`,
        isJoker: true,
        isWildJoker: false,
      });
    }

    return cards;
  }

  private shuffleDeck(deck: Card[]): void {
    for (let i = deck.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [deck[i], deck[j]] = [deck[j], deck[i]];
    }
  }

  // ===========================================================================
  // Game Actions
  // ===========================================================================

  submitAction(playerId: string, action: RummyAction): ActionResult {
    if (this.state.gameStatus.type !== 'active') {
      return { success: false, error: 'Game is not active' };
    }

    const playerIndex = this.state.players.findIndex(p => p.id === playerId);
    if (playerIndex === -1) {
      return { success: false, error: 'Player not found' };
    }
    if (playerIndex !== this.state.currentPlayerIndex) {
      return { success: false, error: 'Not your turn' };
    }

    const player = this.state.players[playerIndex];
    if (player.status !== 'active') {
      return { success: false, error: 'Player is not active' };
    }

    switch (action.type) {
      case 'draw-stock':
        return this.drawFromStock(player);
      case 'draw-discard':
        return this.drawFromDiscard(player);
      case 'discard':
        return this.discard(player, action.cardId!);
      case 'declare':
        return this.declare(player);
      case 'drop':
        return this.drop(player);
      case 'sort':
        this.sortHand(player);
        return { success: true };
      default:
        return { success: false, error: 'Unknown action' };
    }
  }

  private drawFromStock(player: RummyPlayerState): ActionResult {
    if (this.state.turnPhase !== 'draw') {
      return { success: false, error: 'Not in draw phase' };
    }
    if (player.hasDrawn) {
      return { success: false, error: 'Already drawn this turn' };
    }

    // If stock is empty, reshuffle discard pile (keep top card)
    if (this.state.stock.length === 0) {
      if (this.state.discardPile.length <= 1) {
        return { success: false, error: 'No cards available' };
      }
      const topDiscard = this.state.discardPile.pop()!;
      this.state.stock = [...this.state.discardPile];
      this.shuffleDeck(this.state.stock);
      this.state.discardPile = [topDiscard];
    }

    const card = this.state.stock.pop()!;
    player.hand.push(card);
    player.hasDrawn = true;
    this.state.turnPhase = 'discard';

    return { success: true };
  }

  private drawFromDiscard(player: RummyPlayerState): ActionResult {
    if (this.state.turnPhase !== 'draw') {
      return { success: false, error: 'Not in draw phase' };
    }
    if (player.hasDrawn) {
      return { success: false, error: 'Already drawn this turn' };
    }
    if (this.state.discardPile.length === 0) {
      return { success: false, error: 'Discard pile is empty' };
    }

    const card = this.state.discardPile.pop()!;
    player.hand.push(card);
    player.hasDrawn = true;
    this.state.turnPhase = 'discard';

    return { success: true };
  }

  private discard(player: RummyPlayerState, cardId: string): ActionResult {
    if (this.state.turnPhase !== 'discard') {
      return { success: false, error: 'Not in discard phase' };
    }
    if (!player.hasDrawn) {
      return { success: false, error: 'Must draw before discarding' };
    }

    const cardIndex = player.hand.findIndex(c => c.id === cardId);
    if (cardIndex === -1) {
      return { success: false, error: 'Card not in hand' };
    }

    const [card] = player.hand.splice(cardIndex, 1);
    this.state.discardPile.push(card);
    this.state.discardHistory.push(card);
    player.hasDrawn = false;

    // Advance to next active player
    this.advanceToNextPlayer();

    return { success: true };
  }

  private declare(player: RummyPlayerState): ActionResult {
    if (this.state.turnPhase !== 'discard') {
      return { success: false, error: 'Must draw before declaring' };
    }
    if (!player.hasDrawn) {
      return { success: false, error: 'Must draw before declaring' };
    }
    if (player.hand.length !== CARDS_PER_HAND + 1) {
      return { success: false, error: `Must have ${CARDS_PER_HAND + 1} cards to declare (draw first, then declare before discarding)` };
    }

    // Find the best arrangement of all 14 cards (13 in melds + 1 discard)
    const result = this.findBestDeclaration(player.hand);

    if (result.valid) {
      player.melds = result.melds;
      player.status = 'declared';
      player.hand = result.unmeldedCards; // Should be exactly 1 card (the discard)

      // The unmelded card goes to discard
      if (result.unmeldedCards.length === 1) {
        this.state.discardPile.push(result.unmeldedCards[0]);
        this.state.discardHistory.push(result.unmeldedCards[0]);
        player.hand = [];
      }

      // Calculate scores for other players
      for (const other of this.state.players) {
        if (other.id !== player.id && other.status === 'active') {
          other.score += this.calculatePoints(other.hand);
        }
      }

      this.state.gameStatus = { type: 'roundComplete', winnerId: player.id };
      return { success: true, roundComplete: true, declarationValid: true, winnerMelds: result.melds };
    }

    return {
      success: false,
      error: result.reason || 'Invalid declaration: need at least 2 sequences (1 must be pure)',
      declarationValid: false,
    };
  }

  private drop(player: RummyPlayerState): ActionResult {
    player.status = 'dropped';
    player.score += this.state.turnPhase === 'draw' && !player.hasDrawn ? 20 : 40;

    // Check if only one player remains
    const activePlayers = this.state.players.filter(p => p.status === 'active');
    if (activePlayers.length === 1) {
      this.state.gameStatus = { type: 'roundComplete', winnerId: activePlayers[0].id };
      return { success: true, roundComplete: true };
    }

    if (this.state.players[this.state.currentPlayerIndex].id === player.id) {
      this.advanceToNextPlayer();
    }

    return { success: true };
  }

  private advanceToNextPlayer(): void {
    const playerCount = this.state.players.length;
    let nextIndex = (this.state.currentPlayerIndex + 1) % playerCount;
    let attempts = 0;

    while (this.state.players[nextIndex].status !== 'active' && attempts < playerCount) {
      nextIndex = (nextIndex + 1) % playerCount;
      attempts++;
    }

    this.state.currentPlayerIndex = nextIndex;
    this.state.turnPhase = 'draw';
    this.state.players[nextIndex].hasDrawn = false;
  }

  // ===========================================================================
  // Hand Sorting
  // ===========================================================================

  sortHand(player: RummyPlayerState): void {
    player.hand.sort((a, b) => {
      if (a.isJoker && !b.isJoker) return 1;
      if (!a.isJoker && b.isJoker) return -1;
      if (a.suit !== b.suit) return SUITS.indexOf(a.suit) - SUITS.indexOf(b.suit);
      return RANK_VALUES[a.rank] - RANK_VALUES[b.rank];
    });
  }

  // ===========================================================================
  // Meld Validation & Detection
  // ===========================================================================

  /**
   * Find the best declaration arrangement for a hand of 14 cards.
   * Must arrange 13 cards into valid melds and leave 1 card as discard.
   * Requirements: at least 2 sequences, at least 1 pure sequence.
   */
  findBestDeclaration(hand: Card[]): DeclarationResult {
    let bestResult: DeclarationResult = {
      valid: false,
      melds: [],
      unmeldedCards: [...hand],
      unmeldedPoints: this.calculatePoints(hand),
      hasPureSequence: false,
      sequenceCount: 0,
      reason: 'No valid arrangement found',
    };

    // Try each card as the potential discard
    for (let i = 0; i < hand.length; i++) {
      const remaining = [...hand.slice(0, i), ...hand.slice(i + 1)];
      const discardCard = hand[i];
      const result = this.findBestMelds(remaining);

      if (result.valid) {
        return {
          ...result,
          unmeldedCards: [discardCard],
          unmeldedPoints: 0,
        };
      }

      // Track best invalid result (lowest unmelded points)
      if (result.unmeldedPoints < bestResult.unmeldedPoints) {
        bestResult = {
          ...result,
          unmeldedCards: [...result.unmeldedCards, discardCard],
        };
      }
    }

    return bestResult;
  }

  /**
   * Find the best arrangement of exactly 13 cards into melds.
   * Uses backtracking to find optimal grouping.
   */
  findBestMelds(hand: Card[]): DeclarationResult {
    if (hand.length !== CARDS_PER_HAND) {
      return {
        valid: false,
        melds: [],
        unmeldedCards: [...hand],
        unmeldedPoints: this.calculatePoints(hand),
        hasPureSequence: false,
        sequenceCount: 0,
        reason: `Expected ${CARDS_PER_HAND} cards, got ${hand.length}`,
      };
    }

    let bestResult: DeclarationResult | null = null;

    const tryArrangement = (
      remaining: Card[],
      currentMelds: Meld[],
    ): void => {
      // If no cards remaining, check if arrangement is valid
      if (remaining.length === 0) {
        const sequences = currentMelds.filter(m => m.type === 'sequence');
        const pureSequences = sequences.filter(m => m.isPure);
        const result: DeclarationResult = {
          valid: sequences.length >= REQUIRED_SEQUENCES && pureSequences.length >= 1,
          melds: [...currentMelds],
          unmeldedCards: [],
          unmeldedPoints: 0,
          hasPureSequence: pureSequences.length > 0,
          sequenceCount: sequences.length,
        };

        if (result.valid && (!bestResult || !bestResult.valid)) {
          bestResult = result;
        }
        return;
      }

      // Pruning: if we already found a valid result, stop
      if (bestResult?.valid) return;

      // Find all possible melds starting from remaining cards
      const possibleMelds = this.findAllPossibleMelds(remaining);

      for (const meld of possibleMelds) {
        const meldCardIds = new Set(meld.cards.map(c => c.id));
        const newRemaining = remaining.filter(c => !meldCardIds.has(c.id));
        tryArrangement(newRemaining, [...currentMelds, meld]);
      }

      // Also try leaving remaining cards unmelded (base case for partial arrangements)
      if (remaining.length > 0 && currentMelds.length > 0) {
        const sequences = currentMelds.filter(m => m.type === 'sequence');
        const pureSequences = sequences.filter(m => m.isPure);
        const unmeldedPoints = this.calculatePoints(remaining);

        const result: DeclarationResult = {
          valid: false,
          melds: [...currentMelds],
          unmeldedCards: [...remaining],
          unmeldedPoints,
          hasPureSequence: pureSequences.length > 0,
          sequenceCount: sequences.length,
          reason: 'Not all cards melded',
        };

        if (!bestResult || unmeldedPoints < bestResult.unmeldedPoints) {
          bestResult = result;
        }
      }
    };

    tryArrangement(hand, []);

    return bestResult || {
      valid: false,
      melds: [],
      unmeldedCards: [...hand],
      unmeldedPoints: this.calculatePoints(hand),
      hasPureSequence: false,
      sequenceCount: 0,
      reason: 'No valid arrangement found',
    };
  }

  /**
   * Find all possible melds from a set of cards.
   */
  findAllPossibleMelds(cards: Card[]): Meld[] {
    const melds: Meld[] = [];

    // Find sequences (consecutive cards of same suit)
    melds.push(...this.findPossibleSequences(cards));

    // Find sets (same rank, different suits)
    melds.push(...this.findPossibleSets(cards));

    return melds;
  }

  private findPossibleSequences(cards: Card[]): Meld[] {
    const sequences: Meld[] = [];
    const nonJokerCards = cards.filter(c => !c.isJoker && !c.isWildJoker);
    const jokerCards = cards.filter(c => c.isJoker || c.isWildJoker);

    // Group by suit
    const bySuit = new Map<Suit, Card[]>();
    for (const card of nonJokerCards) {
      const existing = bySuit.get(card.suit) || [];
      existing.push(card);
      bySuit.set(card.suit, existing);
    }

    for (const [suit, suitCards] of bySuit) {
      // Sort by rank value
      const sorted = [...suitCards].sort((a, b) => RANK_VALUES[a.rank] - RANK_VALUES[b.rank]);

      // Find pure sequences (no jokers)
      for (let start = 0; start < sorted.length; start++) {
        const seq: Card[] = [sorted[start]];
        for (let next = start + 1; next < sorted.length; next++) {
          if (RANK_VALUES[sorted[next].rank] === RANK_VALUES[seq[seq.length - 1].rank] + 1) {
            seq.push(sorted[next]);
            if (seq.length >= MIN_MELD_SIZE) {
              sequences.push({
                type: 'sequence',
                cards: [...seq],
                isPure: true,
              });
            }
          } else {
            break;
          }
        }

        // Also try Ace-high sequences: Q-K-A
        if (sorted[start].rank === 'Q') {
          const aceHighSeq: Card[] = [sorted[start]];
          const king = sorted.find(c => c.rank === 'K' && !aceHighSeq.some(s => s.id === c.id));
          if (king) {
            aceHighSeq.push(king);
            const ace = sorted.find(c => c.rank === 'A' && !aceHighSeq.some(s => s.id === c.id));
            if (ace) {
              aceHighSeq.push(ace);
              sequences.push({
                type: 'sequence',
                cards: [...aceHighSeq],
                isPure: true,
              });
            }
          }
        }
      }

      // Find impure sequences (with jokers filling gaps)
      if (jokerCards.length > 0) {
        for (let start = 0; start < sorted.length; start++) {
          this.findImpureSequences(sorted, start, jokerCards, suit, sequences);
        }
      }
    }

    return sequences;
  }

  private findImpureSequences(
    sortedSuitCards: Card[],
    startIdx: number,
    jokerCards: Card[],
    _suit: Suit,
    sequences: Meld[],
  ): void {
    const seq: Card[] = [sortedSuitCards[startIdx]];
    let jokersUsed = 0;
    let currentRankVal = RANK_VALUES[sortedSuitCards[startIdx].rank];

    for (let targetRank = currentRankVal + 1; targetRank <= 13; targetRank++) {
      const nextCard = sortedSuitCards.find(
        c => RANK_VALUES[c.rank] === targetRank && !seq.some(s => s.id === c.id)
      );

      if (nextCard) {
        seq.push(nextCard);
        currentRankVal = targetRank;
      } else if (jokersUsed < jokerCards.length) {
        seq.push(jokerCards[jokersUsed]);
        jokersUsed++;
        currentRankVal = targetRank;
      } else {
        break;
      }

      if (seq.length >= MIN_MELD_SIZE && jokersUsed > 0) {
        sequences.push({
          type: 'sequence',
          cards: [...seq],
          isPure: false,
        });
      }
    }
  }

  private findPossibleSets(cards: Card[]): Meld[] {
    const sets: Meld[] = [];
    const nonJokerCards = cards.filter(c => !c.isJoker && !c.isWildJoker);
    const jokerCards = cards.filter(c => c.isJoker || c.isWildJoker);

    // Group by rank
    const byRank = new Map<Rank, Card[]>();
    for (const card of nonJokerCards) {
      const existing = byRank.get(card.rank) || [];
      existing.push(card);
      byRank.set(card.rank, existing);
    }

    for (const [, rankCards] of byRank) {
      // Ensure different suits
      const uniqueSuitCards: Card[] = [];
      const usedSuits = new Set<Suit>();
      for (const card of rankCards) {
        if (!usedSuits.has(card.suit)) {
          uniqueSuitCards.push(card);
          usedSuits.add(card.suit);
        }
      }

      // Pure sets (3 or 4 cards of same rank, different suits)
      if (uniqueSuitCards.length >= MIN_MELD_SIZE) {
        // 3-card sets
        for (let i = 0; i < uniqueSuitCards.length; i++) {
          for (let j = i + 1; j < uniqueSuitCards.length; j++) {
            for (let k = j + 1; k < uniqueSuitCards.length; k++) {
              sets.push({
                type: 'set',
                cards: [uniqueSuitCards[i], uniqueSuitCards[j], uniqueSuitCards[k]],
                isPure: true,
              });
            }
          }
        }

        // 4-card sets
        if (uniqueSuitCards.length >= 4) {
          sets.push({
            type: 'set',
            cards: uniqueSuitCards.slice(0, 4),
            isPure: true,
          });
        }
      }

      // Sets with jokers
      if (uniqueSuitCards.length >= 2 && jokerCards.length > 0) {
        for (let i = 0; i < uniqueSuitCards.length; i++) {
          for (let j = i + 1; j < uniqueSuitCards.length; j++) {
            sets.push({
              type: 'set',
              cards: [uniqueSuitCards[i], uniqueSuitCards[j], jokerCards[0]],
              isPure: false,
            });
            if (jokerCards.length >= 2) {
              sets.push({
                type: 'set',
                cards: [uniqueSuitCards[i], uniqueSuitCards[j], jokerCards[0], jokerCards[1]],
                isPure: false,
              });
            }
          }
        }
      }

      if (uniqueSuitCards.length >= 1 && jokerCards.length >= 2) {
        sets.push({
          type: 'set',
          cards: [uniqueSuitCards[0], jokerCards[0], jokerCards[1]],
          isPure: false,
        });
      }
    }

    return sets;
  }

  // ===========================================================================
  // Scoring
  // ===========================================================================

  calculatePoints(hand: Card[]): number {
    let points = 0;
    for (const card of hand) {
      if (card.isJoker || card.isWildJoker) continue;
      points += CARD_POINTS[card.rank];
    }
    return Math.min(points, 80); // Max 80 points
  }

  // ===========================================================================
  // State Accessors
  // ===========================================================================

  getGameState(): RummyGameState {
    return this.state;
  }

  getVisibleState(playerId: string): VisibleRummyState {
    return {
      players: this.state.players.map(p => ({
        id: p.id,
        name: p.name,
        hand: p.id === playerId ? p.hand : null,
        melds: p.melds,
        cardCount: p.hand.length,
        score: p.score,
        status: p.status,
        hasDrawn: p.hasDrawn,
      })),
      stockCount: this.state.stock.length,
      discardTop: this.state.discardPile.length > 0
        ? this.state.discardPile[this.state.discardPile.length - 1]
        : null,
      wildJoker: this.state.wildJoker,
      currentPlayerIndex: this.state.currentPlayerIndex,
      turnPhase: this.state.turnPhase,
      gameStatus: this.state.gameStatus,
      roundNumber: this.state.roundNumber,
    };
  }

  getCurrentPlayer(): string | null {
    if (this.state.gameStatus.type !== 'active') return null;
    return this.state.players[this.state.currentPlayerIndex]?.id || null;
  }

  isGameOver(): boolean {
    return this.state.gameStatus.type === 'roundComplete' || this.state.gameStatus.type === 'gameOver';
  }

  isRoundInProgress(): boolean {
    return this.state.gameStatus.type === 'active';
  }

  getWinner(): string | null {
    if (this.state.gameStatus.type === 'roundComplete') return this.state.gameStatus.winnerId;
    if (this.state.gameStatus.type === 'gameOver') return this.state.gameStatus.winnerId;
    return null;
  }

  getDiscardHistory(): Card[] {
    return [...this.state.discardHistory];
  }

  getPlayerHand(playerId: string): Card[] {
    const player = this.state.players.find(p => p.id === playerId);
    return player ? [...player.hand] : [];
  }
}
